'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { cn, vaultSpring, triggerHaptic } from '../lib/utils';
import { useSlideToConfirm } from '../hooks';

export interface SlideToConfirmProps {
  onConfirm: () => void;
  label?: string;
  confirmLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

export const SlideToConfirm: React.FC<SlideToConfirmProps> = ({
  onConfirm,
  label = 'Slide to Confirm',
  confirmLabel = 'Confirmed!',
  disabled = false,
  loading = false,
  className,
}) => {
  const {
    trackRef,
    progress,
    isDragging,
    isConfirmed,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    reset,
  } = useSlideToConfirm(() => {
    triggerHaptic(10);
    onConfirm();
  });

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabled || loading) return;
    handleDragMove(e.touches[0].clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (disabled || loading || !isDragging) return;
    handleDragMove(e.clientX);
  };

  React.useEffect(() => {
    if (loading) return;
    
    const handleGlobalMouseUp = () => handleDragEnd();
    const handleGlobalTouchEnd = () => handleDragEnd();

    if (isDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchend', handleGlobalTouchEnd);
    }

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, handleDragEnd, loading]);

  // Reset after successful confirmation (for re-use)
  React.useEffect(() => {
    if (isConfirmed && !loading) {
      const timer = setTimeout(reset, 2000);
      return () => clearTimeout(timer);
    }
  }, [isConfirmed, loading, reset]);

  const thumbX = progress * (trackRef.current?.offsetWidth || 0 - 56);

  return (
    <div
      ref={trackRef}
      className={cn(
        'slide-track relative select-none',
        disabled && 'opacity-50 cursor-not-allowed',
        isConfirmed && 'bg-vault-success/20 border-vault-success/30',
        className
      )}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
    >
      {/* Progress Fill */}
      <motion.div
        className={cn(
          'absolute inset-y-0 left-0 rounded-full',
          isConfirmed ? 'bg-vault-success/20' : 'bg-gold-500/10'
        )}
        style={{ width: `${progress * 100}%` }}
        transition={vaultSpring}
      />

      {/* Label */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.span
          className={cn(
            'text-sm font-medium',
            isConfirmed ? 'text-vault-success' : 'text-text-secondary'
          )}
          initial={{ opacity: 1 }}
          animate={{ opacity: isConfirmed ? 1 : 1 - progress * 2 }}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </span>
          ) : isConfirmed ? (
            confirmLabel
          ) : (
            label
          )}
        </motion.span>
      </div>

      {/* Thumb */}
      {!isConfirmed && !loading && (
        <motion.div
          className={cn(
            'slide-thumb',
            isDragging && 'cursor-grabbing'
          )}
          style={{ x: thumbX }}
          animate={{ x: isDragging ? thumbX : progress * ((trackRef.current?.offsetWidth || 200) - 56) }}
          transition={isDragging ? { duration: 0 } : vaultSpring}
          onMouseDown={() => !disabled && handleDragStart()}
          onTouchStart={() => !disabled && handleDragStart()}
        >
          <ChevronRight className="w-5 h-5" />
          <ChevronRight className="w-5 h-5 -ml-3 opacity-50" />
        </motion.div>
      )}

      {/* Success Checkmark */}
      {isConfirmed && !loading && (
        <motion.div
          className="absolute left-1 top-1 bottom-1 w-12 bg-vault-success rounded-full flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={vaultSpring}
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>
      )}
    </div>
  );
};
